import moment from 'moment'
import { CSSProperties, useRef, useState, useCallback, useMemo, memo, useEffect } from 'react'
import DP from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import React from 'react'
import InputMask from 'react-input-mask'
import { validateDateString, validateTimeString } from '../Utils'

// Keep utility functions as they are for compatibility
function toMomentFormat(fmt: string): string {
    return fmt.replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD').replace(/d/g, 'D')
}

function toDateFnsFormat(fmt: string): string {
    return fmt.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/D/g, 'd')
}

function formatToMask(fmt: string): string {
    return (
        fmt
            .replace(/yyyy/g, '9999')
            .replace(/MM/g, '99')
            .replace(/dd/g, '99')
            .replace(/d/g, '9')
            // Time
            .replace(/HH/g, '99') // hours 00–23
            .replace(/mm/g, '99') // minutes 00–59
            .replace(/ss/g, '99')
    ) // seconds 00–59
}

// Define props interface for better type checking
interface DatePickerProps {
    id?: string
    dateFormat: string
    name?: string
    title?: string
    timeformat?: string
    setStartingDate: (value: string) => void
    tabIndex?: number
    classes?: string | undefined
    style?: CSSProperties | undefined
    inputClass?: string | undefined
    showIcon?: boolean
    startingDate: Date | string | null | undefined
    showTime?: boolean
    interval?: number
    placeholder: string //Placeholder is mandatory, if not required, place empty
    value?: string
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>, selectedDates: any) => void
    showUnicornDateIcon?: boolean
    selectedDate?: Date | null | undefined
    size?: string
    disabled?: boolean
    className?: string
    unicornIconClasses?: string
    popperClass?: string
    popperModifiers?: any
    onCalendarClick?: (event: any) => void
    showTimeSelectOnly?: boolean
    showMasking?: boolean
    isClearable?: boolean
    readOnly?: boolean
    onValidationError?: (error: string) => void // Callback for validation errors
    calendarClassName?: string
    calendarClosed?: boolean
    monthsShown?: number
    dateFormatCalendar?: string
}

// Extract CustomInput component for reusability
const CustomInput = React.forwardRef(
    (
        {
            value,
            onClick,
            onChange,
            onBlur,
            placeholder,
            disabled,
            onKeyDown,
            showMasking,
            dateFormat,
            inputClass,
            tabIndex,
            onValidationError,
            showTimeSelectOnly,
            onRawInputChange,
            rawInputValue,
            inputElementRef,
        }: any,
        ref: any
    ) => {
        // Common styles for both input types
        const commonStyles = {
            width: '100%',
            boxSizing: 'border-box' as 'border-box',
            outline: 'none',
            display: 'block',
        }

        // Use raw input value if provided (when there's an error), otherwise use the value from react-datepicker
        const displayValue = rawInputValue !== undefined && rawInputValue !== null ? rawInputValue : (value || '')

        // Create a merged ref callback that sets both refs
        const setRefs = useCallback((element: HTMLInputElement | null) => {
            // Set the ref from react-datepicker
            if (typeof ref === 'function') {
                ref(element)
            } else if (ref) {
                ref.current = element
            }
            // Set our inputElementRef
            if (inputElementRef) {
                if (typeof inputElementRef === 'function') {
                    inputElementRef(element)
                } else if (inputElementRef && 'current' in inputElementRef) {
                    inputElementRef.current = element
                }
            }
        }, [ref, inputElementRef])

        // Handle input change - track raw value and update parent
        const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value || ''
            
            // Update raw input value in parent
            if (onRawInputChange) {
                onRawInputChange(inputValue)
            }
            
            // Call original onChange without validation
            if (onChange) {
                onChange(e)
            }
        }, [onChange, onRawInputChange])

        // Handle blur event - validate when user leaves the field
        const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
            const inputValue = e.target.value || ''
            
            // Validate only if masking is enabled and we have a validation callback
            if (showMasking && onValidationError && inputValue.trim() !== '') {
                let error = ''
                
                if (showTimeSelectOnly) {
                    // Validate time-only format
                    error = validateTimeString(inputValue)
                } else {
                    // Validate date or datetime format
                    error = validateDateString(inputValue, dateFormat)
                }
                
                // Call validation callback with error (empty string if valid)
                // If valid, this will clear the error state and allow react-datepicker to control the value
                onValidationError(error)
            } else if (onValidationError && inputValue.trim() === '') {
                // Clear error when field is empty
                onValidationError('')
            }
            
            // Call original onBlur if provided
            if (onBlur) {
                onBlur(e)
            }
        }, [showMasking, onValidationError, dateFormat, showTimeSelectOnly, onBlur])

        if (showMasking && !disabled) {
            // Create a mask based on the date format
            const mask = formatToMask(dateFormat)

            return (
                <div style={{ width: '100%', display: 'block' }}>
                    <InputMask
                        mask={mask}
                        value={displayValue}
                        onChange={handleInputChange}
                        onSelect={handleInputChange} // To handle cursor position correctly
                        onBlur={handleBlur}
                        onClick={onClick}
                        placeholder={placeholder} // Use the correct placeholder
                        disabled={disabled}
                        onKeyDown={onKeyDown}
                        style={{ width: '100%', display: 'block' }}
                        className={inputClass || ''}
                        tabIndex={tabIndex}
                    >
                        {(inputProps: any) => (
                            <input
                                {...inputProps}
                                ref={setRefs}
                                style={commonStyles}
                                autoComplete="off"
                                onBlur={handleBlur}
                            />
                        )}
                    </InputMask>
                </div>
            )
        }

        // If masking is disabled, return a standard input with the same styling
        return (
            <input
                ref={setRefs}
                value={displayValue}
                onClick={onClick}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                onKeyDown={onKeyDown}
                className={inputClass || ''}
                style={commonStyles}
                autoComplete="off"
                tabIndex={tabIndex}
            />
        )
    }
)

// Calendar icon component - memoized for better performance
const CalendarIcon = memo(() => (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 48 48">
        <mask id="ipSApplication0">
            <g fill="none" stroke="#fff" strokeLinejoin="round" strokeWidth="4">
                <path strokeLinecap="round" d="M40.04 22v20h-32V22"></path>
                <path
                    fill="#fff"
                    d="M5.842 13.777C4.312 17.737 7.263 22 11.51 22c3.314 0 6.019-2.686 6.019-6a6 6 0 0 0 6 6h1.018a6 6 0 0 0 6-6c0 3.314 2.706 6 6.02 6c4.248 0 7.201-4.265 5.67-8.228L39.234 6H8.845l-3.003 7.777Z"
                ></path>
            </g>
        </mask>
        <path fill="currentColor" d="M0 0h48v48H0z" mask="url(#ipSApplication0)"></path>
    </svg>
))

function DatePicker(props: DatePickerProps) {
    const datepickerRef = useRef<any>(null)
    const inputElementRef = useRef<HTMLInputElement | null>(null)

    // Keep original local state to maintain the same behavior
    const [selectedDate, setSelectedDate] = useState<any>(null) //FP-11937 maintaining local state & tried using only local state to display date, but it need useEffect to set default, so default selected date does not appending to datePIcker
    
    // Track raw input value to preserve invalid input
    const [rawInputValue, setRawInputValue] = useState<string>('')
    const [hasValidationError, setHasValidationError] = useState<boolean>(false)

    // Memoize handlers for better performance
    const handleDateChange = useCallback(
        (event: any) => {
            // If there's a validation error, only allow changes from calendar selection (not from typing)
            // This prevents react-datepicker from reverting to previous valid date when user types invalid input
            if (hasValidationError) {
                // Only process if event is null (clear) or if it's a valid date that's significantly different
                // from what would be parsed from rawInputValue (indicating calendar selection)
                if (!event) {
                    // Allow clearing
                    setSelectedDate(null)
                    setRawInputValue('')
                    props.setStartingDate('')
                    setHasValidationError(false)
                    if (props.onValidationError) {
                        props.onValidationError('')
                    }
                } else {
                    // Check if this is a calendar selection by seeing if it's a valid date
                    // that's different from what's in rawInputValue
                    try {
                        const formattedFromEvent = moment(event).format(toMomentFormat(props.dateFormat))
                        // If the formatted date from event doesn't match rawInputValue, it's likely from calendar
                        // In that case, validate the rawInputValue first - if it's now valid, use it; otherwise use calendar date
                        const rawError = props.showTimeSelectOnly 
                            ? validateTimeString(rawInputValue)
                            : validateDateString(rawInputValue, props.dateFormat)
                        
                        if (!rawError && formattedFromEvent === rawInputValue) {
                            // User typed valid date that matches - clear error and use it
                            setSelectedDate(event)
                            props.setStartingDate(formattedFromEvent)
                            setHasValidationError(false)
                            if (props.onValidationError) {
                                props.onValidationError('')
                            }
                        } else if (formattedFromEvent !== rawInputValue) {
                            // This is from calendar selection - use it and clear error
                            setSelectedDate(event)
                            props.setStartingDate(formattedFromEvent)
                            setRawInputValue(formattedFromEvent)
                            setHasValidationError(false)
                            if (props.onValidationError) {
                                props.onValidationError('')
                            }
                        }
                        // Otherwise, ignore to preserve invalid input
                    } catch (e) {
                        // Ignore invalid dates
                    }
                }
                return
            }

            // Normal flow when no validation error
            if (!event) {
                setSelectedDate(null)
                setRawInputValue('')
                props.setStartingDate('')
                setHasValidationError(false)
                // Clear validation error when date is cleared
                if (props.onValidationError) {
                    props.onValidationError('')
                }
            } else {
                setSelectedDate(event)
                if (event !== null) {
                    const formattedDate = moment(event).format(toMomentFormat(props.dateFormat)) //input accepts moment format, but DatePicker library accepts date-fns format
                    if (formattedDate) {
                        props.setStartingDate(formattedDate)
                        setRawInputValue(formattedDate)
                        setHasValidationError(false)
                        // Clear validation error when valid date is selected from calendar
                        if (props.onValidationError) {
                            props.onValidationError('')
                        }
                    }
                }
            }
            if (datepickerRef.current) {
                datepickerRef.current.setFocus(); // react-datepicker provides a setFocus method
              }
        },
        [props.dateFormat, props.setStartingDate, props.onValidationError, props.showTimeSelectOnly, hasValidationError, rawInputValue]
    )

    const handleDateIconClick = useCallback(() => {
        // FP-11937 if its already open then clicking on date icon does not closes, it reopens this
        datepickerRef.current.setOpen(true)
    }, [])

    // Handle raw input value changes from user typing
    const handleRawInputChange = useCallback((inputValue: string) => {
        setRawInputValue(inputValue)
        
        // If there was a validation error, check if the new input is valid
        // If valid, clear the error immediately (user fixed the issue)
        if (hasValidationError && inputValue.trim() !== '') {
            let error = ''
            if (props.showTimeSelectOnly) {
                error = validateTimeString(inputValue)
            } else {
                error = validateDateString(inputValue, props.dateFormat)
            }
            
            if (!error) {
                // Input is now valid - clear error state
                // The next onChange from react-datepicker will update the date properly
                setHasValidationError(false)
                if (props.onValidationError) {
                    props.onValidationError('')
                }
            }
        }
    }, [hasValidationError, props.showTimeSelectOnly, props.dateFormat, props.onValidationError])

    // Handle validation errors - update state to preserve invalid input
    const handleValidationError = useCallback((error: string) => {
        setHasValidationError(!!error)
        if (props.onValidationError) {
            props.onValidationError(error)
        }
    }, [props.onValidationError])

    // Validate input value - can be called from blur or calendar close
    const validateInputValue = useCallback((inputValue: string) => {
        if (!props.showMasking || !props.onValidationError || !inputValue || inputValue.trim() === '') {
            if (props.onValidationError && (!inputValue || inputValue.trim() === '')) {
                props.onValidationError('')
            }
            return
        }

        let error = ''
        if (props.showTimeSelectOnly) {
            error = validateTimeString(inputValue)
        } else {
            error = validateDateString(inputValue, props.dateFormat)
        }
        
        handleValidationError(error)
    }, [props.showMasking, props.onValidationError, props.showTimeSelectOnly, props.dateFormat, handleValidationError])


    useEffect(() => {
        setSelectedDate(props.startingDate)
        if (props.startingDate) {
            const formattedFromEvent = moment(props.startingDate).format(toMomentFormat(props.dateFormat))
            if (formattedFromEvent !== rawInputValue) setRawInputValue(formattedFromEvent)
            if (props.onValidationError) {
                props.onValidationError('')
            }
        } 
    }, [props.startingDate?.toString()])


    // Handle calendar close - validate when calendar closes
    const handleCalendarClose = useCallback(() => {
        // When calendar closes, validate the current input value
        // Use setTimeout to ensure we get the value after calendar has fully closed
        setTimeout(() => {
            // Try to get value from the input element directly, or use rawInputValue
            let valueToValidate = rawInputValue
            
            // If we have a direct ref to the input element, use it
            if (inputElementRef.current && inputElementRef.current.value) {
                const inputVal = inputElementRef.current.value
                // Use input value if it's different from rawInputValue (user typed while calendar was open)
                if (inputVal !== rawInputValue) {
                    valueToValidate = inputVal
                    // Also update rawInputValue to keep it in sync
                    setRawInputValue(inputVal)
                } else {
                    valueToValidate = inputVal
                }
            }
            
            // Validate if we have a value
            if (valueToValidate && valueToValidate.trim() !== '') {
                validateInputValue(valueToValidate)
            }
        }, 0)
    }, [rawInputValue, validateInputValue])

    // Memoize CustomInput props to prevent unnecessary renders
    const customInputProps = useMemo(
        () => ({
            showMasking: props.showMasking,
            dateFormat: props.dateFormat,
            inputClass: props.inputClass,
            onValidationError: handleValidationError,
            showTimeSelectOnly: props.showTimeSelectOnly,
            onRawInputChange: handleRawInputChange,
            rawInputValue: hasValidationError ? rawInputValue : undefined, // Only use raw value when there's an error
            inputElementRef: inputElementRef,
        }),
        [props.showMasking, props.dateFormat, props.inputClass, handleValidationError, props.showTimeSelectOnly, handleRawInputChange, hasValidationError, rawInputValue]
    )

    // Add a class if monthsShown > 1
    const calendarClassName = [
        props.calendarClassName,
        props.monthsShown && props.monthsShown > 1 ? 'has-multiple-months' : ''
    ].filter(Boolean).join(' ');

    return (
        <span title={props.title} className={props.classes} style={props.style}>
            <DP
                showMonthDropdown //FP-11937 Month & Year dropdown is mandatory across Foundations whenever this DatePicker is used
                showYearDropdown
                dateFormatCalendar={props.dateFormatCalendar ?? ' '} //FP-11937 this hides Datepicker HEADER which indicates Month Year we can pass customised options, adding globally
                ref={datepickerRef}
                className={props.className ?? ''}
                scrollableMonthYearDropdown
                scrollableYearDropdown
                dateFormat={toDateFnsFormat(props.dateFormat)} //FP-11937 instead of replaceAll, it handles all types of date format
                onSelect={props?.onCalendarClick} //this is for clicking on calendar date
                selected={props?.startingDate ? props?.startingDate : selectedDate} // Use native Date object
                onChange={(e) => handleDateChange(e)}
                onCalendarClose={handleCalendarClose} // Validate when calendar closes
                showTimeSelect={props.showTime || false}
                timeFormat={props.timeformat || 'HH:mm'}
                timeIntervals={props.interval || 5}
                placeholderText={!props.placeholder ? 'Select Date and Time' : props.placeholder}
                onKeyDown={(e) => props.onKeyDown && props.onKeyDown(e, selectedDate)}
                popperPlacement="bottom"
                disabled={props.disabled}
                showPopperArrow={false}
                popperClassName={props.popperClass ?? ''}
                popperModifiers={props.popperModifiers ?? undefined}
                yearDropdownItemNumber={new Date().getFullYear() - 1900 + 1}
                name={props.name}
                tabIndex={props.tabIndex}
                showTimeSelectOnly={props.showTimeSelectOnly || false}
                minDate={new Date('1900-01-01')}
                autoComplete="off"
                customInput={<CustomInput {...customInputProps} />}
                isClearable={props.isClearable || false}
                readOnly={!!props.readOnly}
                calendarClassName={calendarClassName}
                monthsShown={props.monthsShown}
                {...(props.calendarClosed && { open: false, preventOpenOnFocus: true })}
            />
            {props.showIcon && (
                <span onClick={handleDateIconClick}>
                    <CalendarIcon />
                </span>
            )}
        </span>
    )
}

export default memo(DatePicker)
